import { Component, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { Subject } from 'rxjs';
import { differenceInDays } from 'date-fns';
import { GraphTrendIconComponent } from '../../../../../_shared/components/graphs/graph-trend-icon/graph-trend-icon.component';
import { GroupedBarChartComponent } from '../../../../../_shared/components/graphs/grouped-bar-chart/grouped-bar-chart.component';
import { EmptyStateComponent } from '../../../../../_shared/components/states/empty-state/empty-state.component';
import { AccountFilterComponent } from '../../../../../_shared/components/filters/account-filter/account-filter.component';
import { DateFilterComponent } from '../../../../../_shared/components/filters/date-filter/date-filter.component';
import { CostTrackingService } from '../../../services/cost-tracking.service';
import { FilterService } from '../../../../../_shared/services/filter.service';
import { ErrorHandlerService } from '../../../../../_shared/services/error-handler.service';
import { BudgetVsActualInsightsResponse } from '../../../models/cost-tracking-response.model';
import { AccountFilterData, DateIntervalFilterData, WidgetFilterData } from '../../../../../_shared/models/filters/widget-filter-data.model';
import { DateInterval } from '../../../../../_shared/enums';
import { TimeSeriesGroupingHelper, GroupedChartData } from '../../../../../_shared/helpers/chart-grouping/time-series-grouping.helper';
import { formatValue } from '../../../../../_shared/helpers/kpiWidget/kpi-formatters.helper';
import { isEmptyObject } from '../../../../../_shared/helpers/object.helper';
import { AuthService } from '../../../../../_shared/services/auth.service';
import { calculateChartYTicks } from '../../../../../_shared/helpers/chart-y-ticks.helper';
import { TranslocoService, TranslocoPipe } from '@jsverse/transloco';
import { hasCompleteDateRange } from '../../../../../_shared/helpers/widget-filter.helper';
import { AccountDateFilterRequest } from '../../../../../_shared/models/account-date-filter-request.model';

@Component({
  selector: 'app-budgeted-vs-actual-modal',
  standalone: true,
  imports: [
    TranslocoPipe,
    CommonModule,
    GraphTrendIconComponent,
    GroupedBarChartComponent,
    EmptyStateComponent,
    AccountFilterComponent,
    DateFilterComponent
  ],
  templateUrl: './budgeted-vs-actual-modal.component.html',
  styleUrl: './budgeted-vs-actual-modal.component.scss'
})
export class BudgetedVsActualModalComponent {
  private readonly transloco = inject(TranslocoService);
  private activeModal = inject(NgbActiveModal);
  private filterService = inject(FilterService);
  private costTrackingService = inject(CostTrackingService);
  private errorHandlerService: ErrorHandlerService = inject(ErrorHandlerService);
  private authService = inject(AuthService);
  private cancelSubject: Subject<void> = new Subject<void>();

  title: string = inject(TranslocoService).translate('costTracking.charts.budgetedVsActual');
  isLoading: boolean = true;
  accountFilter: AccountFilterData;
  dateFilter: DateIntervalFilterData;
  widgetFilter: WidgetFilterData;
  defaultInterval: DateInterval = DateInterval.Month;
  disabledIntervals: DateInterval[] = [DateInterval.Half];
  currencySymbol: string = '';
  currencyCode: string = '';

  chartData: GroupedChartData[] = [];
  readonly barWidth = 16;
  readonly chartHeight = 335;
  private readonly defaultYTicks = [100, 80, 60, 40, 20, 0];
  yTicks: number[] = this.defaultYTicks;

  // Updated colors: Budgeted #3873C9, Actual #DCC2E3
  readonly subgroups = [
    { name: 'budgeted', legend: this.transloco.translate('costTracking.charts.budgetedCost'), color: '#3873C9' },
    { name: 'actual', legend: this.transloco.translate('costTracking.charts.actualCost'), color: '#DCC2E3' }
  ];

  totalValue: number = 0;
  percentageChange: number = 0;

  constructor() {
    this.accountFilter = {} as AccountFilterData;
    this.dateFilter = {} as DateIntervalFilterData;
    this.widgetFilter = {} as WidgetFilterData;
    this.currencyCode = this.authService.getUserCurrencyCode();
    this.currencySymbol = this.authService.getUserCurrencySymbol();
  }

  async ngOnInit(): Promise<void> {
    this.widgetFilter = this.filterService.filter;
    await this.loadData();
  }

  async onAccountFilterChange(filter: AccountFilterData): Promise<void> {
    this.accountFilter = filter;
    this.updateFilter();
  }

  async onDateFilterChange(filter: DateIntervalFilterData): Promise<void> {
    this.dateFilter = filter;
    this.updateFilter();
  }

  async updateFilter(): Promise<void> {
    if (!isEmptyObject(this.dateFilter) && !isEmptyObject(this.accountFilter)) {
      this.widgetFilter = {
        ...this.dateFilter,
        ...this.accountFilter
      } as WidgetFilterData;

      this.filterService.updateFilter(this.widgetFilter);

      await this.loadData();
    }
  }

  private async loadData(): Promise<void> {
    if (!hasCompleteDateRange(this.widgetFilter)) return;

    this.isLoading = true;

    try {
      this.cancelSubject.next();

      const request: AccountDateFilterRequest = {
        accountIds: this.widgetFilter.accountIds ?? [],
        timeFrom: this.widgetFilter.startDate,
        timeTo: this.widgetFilter.endDate,
        interval: this.widgetFilter.interval,
      };

      const [summaryResponse, budgetedResponse, actualResponse] = await Promise.all([
        this.costTrackingService.getBudgetVsActualSummary(request, this.cancelSubject),
        this.costTrackingService.getBudgetedInsights(request, this.cancelSubject),
        this.costTrackingService.getActualInsights(request, this.cancelSubject)
      ]);

      const budgetedData = budgetedResponse.data ?? [];
      const actualData = actualResponse.data ?? [];
      const summary = summaryResponse.data;

      this.chartData = this.processResponses(budgetedData, actualData);
      this.calculateYTicks();
      this.percentageChange = summary?.percentageChange ?? 0;
      this.totalValue = summary?.currentValue ?? 0;

      this.isLoading = false;
    } catch (error) {
      this.errorHandlerService.handleError(error, {
        callback: () => {
          this.isLoading = false;
          this.chartData = [];
        }
      });
    }
  }

  private processResponses(
    budgetedData: BudgetVsActualInsightsResponse[],
    actualData: BudgetVsActualInsightsResponse[]
  ): GroupedChartData[] {
    const daysDifference = differenceInDays(this.widgetFilter.endDate!, this.widgetFilter.startDate!);

    const datasets = [
      { data: budgetedData, label: 'budgeted' },
      { data: actualData, label: 'actual' }
    ];

    // For 24 hours (1 day): show all 24 individual hours in modal
    if (daysDifference === 0) {
      return TimeSeriesGroupingHelper.groupByHours(datasets, true);
    }

    // For 1 month (up to 31 days): show every day
    if (daysDifference <= 31) {
      return TimeSeriesGroupingHelper.groupByDays(datasets, this.widgetFilter.startDate!, this.widgetFilter.endDate!);
    }

    // For longer periods: group by weeks
    return TimeSeriesGroupingHelper.groupByWeeks(datasets, this.widgetFilter.startDate!, this.widgetFilter.endDate!);
  }

  private calculateYTicks(): void {
    if (this.chartData.length === 0) {
      this.yTicks = this.defaultYTicks;
      return;
    }

    const allValues = this.chartData.flatMap(d => [d['budgeted'] ?? 0, d['actual'] ?? 0]);
    this.yTicks = calculateChartYTicks(allValues, this.defaultYTicks);
  }

  formatPercentChange(): string {
    const sign = this.percentageChange > 0 ? '+' : '';
    return `${sign}${this.percentageChange.toLocaleString('default', { minimumFractionDigits: 0, maximumFractionDigits: 2 })}%`;
  }

  isUptrendPositive(): boolean {
    // For budget tracking, being under budget (negative change) is positive
    return false;
  }

  formatValue(): string {
    return formatValue(this.totalValue, { currencySymbol: this.currencySymbol });
  }

  hasData(): boolean {
    if (!this.chartData || this.chartData.length === 0) return false;

    return this.chartData.some(item => {
      return Object.keys(item).some(key => {
        if (key === 'date' || key === 'originalTimestamp' || key === 'displayLabel' || key === 'tooltipDateTime') return false;
        const value = Number(item[key]) || 0;
        return value > 0;
      });
    });
  }

  close(): void {
    this.activeModal.close();
  }
}